import { useEffect, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { AuthScaffold } from './AuthScaffold'
import { destinationAfterSignIn } from './afterAuth'
import { useAuth } from '@/auth/AuthProvider'
import { FxAlert, FxButton, FxSpinner } from '@/components/fx'
import { ApiError } from '@/lib/api'
import { pendingUpload } from '@/lib/pendingUpload'

/**
 * Where the gateway's OAuth callback sends the browser:
 * `…/auth/callback?code=<one-time code>[&returnTo=/path]`, or
 * `?error=<reason>`.
 *
 * The code is single-use and lives 60 seconds, so it is exchanged exactly
 * once — hence the guard ref. React 18's StrictMode runs effects twice in
 * development, and a second exchange of a consumed code fails, which would
 * show an error on a sign-in that actually worked.
 */
export function OAuthCallbackPage() {
  const [params] = useSearchParams()
  const navigate = useNavigate()
  const { completeOAuthCode } = useAuth()
  const [error, setError] = useState<string | null>(null)
  const exchanged = useRef(false)

  useEffect(() => {
    if (exchanged.current) return
    exchanged.current = true

    const failure = params.get('error')
    if (failure) {
      setError(
        failure === 'email_in_use'
          ? 'Für diese E-Mail-Adresse gibt es schon ein Konto. Bitte mit E-Mail und Passwort anmelden.'
          : 'Die Anmeldung wurde abgebrochen.',
      )
      return
    }

    const code = params.get('code')
    if (!code) {
      setError('Die Anmeldung ist unvollständig zurückgekommen.')
      return
    }

    void (async () => {
      try {
        await completeOAuthCode(code)
        // The funnel lets someone pick their form before they have an
        // account, so a file may be waiting from before the redirect.
        const waiting = await pendingUpload.take()
        navigate(
          destinationAfterSignIn({
            pendingUpload: waiting != null,
            next: params.get('returnTo'),
          }),
          { replace: true },
        )
      } catch (cause) {
        setError(ApiError.from(cause).message)
      }
    })()
  }, [params, completeOAuthCode, navigate])

  if (error) {
    return (
      <AuthScaffold title="Anmeldung fehlgeschlagen">
        <FxAlert tone="danger">{error}</FxAlert>
        <FxButton block onClick={() => navigate('/login', { replace: true })}>
          Zurück zur Anmeldung
        </FxButton>
      </AuthScaffold>
    )
  }

  return (
    <AuthScaffold title="Anmeldung wird abgeschlossen">
      <FxSpinner label="Sitzung wird eingerichtet …" />
    </AuthScaffold>
  )
}
